import React, { useEffect, useState, useRef } from 'react';

import ProductCardsDMobile from '../../../../shared/components/ProductCardsD/ProductCardsDMobile';
import { categoryCards } from './CategoryData';
import constructionProductsService from '../../services/construction-products.service';
import { ConstructionProductDto } from '../../dto/construction-products.dto';
import { useCart } from '../../../../contexts/CartContext';
import { useWishlist } from '../../../wishlist/WishlistContext';
import { useProductNavigation } from '../../../../shared/hooks/useProductNavigation';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CategoryBannerMobile: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState(categoryCards[0]?.name || '');
  const [products, setProducts] = useState<ConstructionProductDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { addToCart } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isItemInWishlist } = useWishlist();
  const navigateToProduct = useProductNavigation();
  const lastWishlistActionRef = useRef<number>(0);

  useEffect(() => {
    handleCategoryChange(selectedCategory);
  }, [selectedCategory]);

  const handleCategoryChange = async (category: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await constructionProductsService.getProductsByCategoryName(category);
      if (response.success && response.data) {
        setProducts(response.data);
      } else {
        setError('Failed to fetch products');
        setProducts([]);
      }
    } catch (error) {
      setError('Error fetching products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product: ConstructionProductDto) => {
    try {
      addToCart(product, 1);
    } catch (error) {
      setError('Error adding to cart');
    }
  };

  const handleWishlistToggle = (product: ConstructionProductDto) => {
    try {
      const now = Date.now();
      if (now - lastWishlistActionRef.current < 300) return;
      lastWishlistActionRef.current = now;

      const isWishlisted = isItemInWishlist(product._id);
      
      if (isWishlisted) {
        removeFromWishlist(product._id);
        toast.info('Removed from wishlist', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      } else {
        const productForWishlist = {
          id: product._id,
          _id: product._id,
          title: product.title || 'Unnamed Product',
          slug: product.slug || `product-${product._id}`,
          description: product.description || '',
          price: product.price || 0,
          compareAtPrice: product.compareAtPrice ?? null,
          quantity: product.quantity ?? 0,
          images: product.images || [],
          imageUrl: product.imageUrl || product.images?.[0]?.url || '',
          isActive: true,
          isFeatured: false,
          hasVariants: false,
          categories: [],
          tags: []
        } as any;

        addToWishlist(productForWishlist);
        toast.success('Added to wishlist', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      }
    } catch (error) {
      // Wishlist update failed silently
    }
  };

  const handleMoreInfo = (product: ConstructionProductDto) => {
    if (product.slug) {
      navigateToProduct(product.slug);
    } else {
    }
  };

  const transformProducts = (apiProducts: ConstructionProductDto[]) => {
    return apiProducts.map(product => ({
      id: parseInt(product._id, 16) || Math.random(),
      name: product.title,
      image: product.imageUrl || product.images?.[0]?.url || 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868',
      alt: product.title,
      slug: product.slug || `product-${product._id}`,
      price: product.price ? `₹${Math.round(product.price)}` : undefined,
      _id: product._id,
      productData: product
    }));
  };

  const productCards = products.length > 0 ? transformProducts(products) : [];

  return (
    <ProductCardsDMobile 
      categoryCards={categoryCards}
      productCards={productCards}
      heading="Light Up Your Homes in Minutes"
      onCategoryClick={setSelectedCategory}
      loading={loading}
      error={error}
      onAddToCart={handleAddToCart}
      onWishlistToggle={handleWishlistToggle}
      onMoreInfo={handleMoreInfo}
      isItemInWishlist={isItemInWishlist}
      products={products}
    />
  );
};

export default CategoryBannerMobile;